import { uniqBy } from 'lodash';
import { apolloClient } from '~/apollo-client';
import {
  ContractTag,
  Flex_FetchVnmTermsQuery,
  Flex_FetchVnmTermsQueryVariables,
  SubscriberRequirementStatus,
} from '~/typings/graphql';
import { STATE_IDS } from '~/utils/constants';
import { FETCH_TERMS } from './fetch-vnm-terms.queries';

const AGENCY_CONTRACT_ID = 30;
// States where we don't support using agency and as a result want to filter it out of general contracts.
const NO_AGENCY_STATES: number[] = [STATE_IDS.IL, STATE_IDS.MA];

export const fetchVnmTerms = async (
  params: FetchVnmTermsParams,
): Promise<FetchVnmTermsResponse> => {
  const { stateId, contractTags, utilityAccountId } = params;

  const { data } = await apolloClient.query<
    Flex_FetchVnmTermsQuery,
    Flex_FetchVnmTermsQueryVariables
  >({
    query: FETCH_TERMS,
    variables: { stateId, tags: contractTags ?? [], utilityAccountId },
  });

  const agencyCommunitySolarContractType = 'vnm';
  const isOregon = STATE_IDS.OR === stateId;
  const termsAndPrivacyContractIds = [31, 43];
  const generalContracts = data.generalContractVersions.filter(contractVersion =>
    termsAndPrivacyContractIds.includes(contractVersion?.contractId),
  );
  const stateGeneralContractVersions = data.generalContractVersions.filter(
    contractVersion =>
      !(isOregon && agencyCommunitySolarContractType === contractVersion?.contractType),
  );

  const requiredContractVersions: ContractVersionWithSignature[] =
    data.utilityAccount.subscriberContractRequirement.dependencies
      .filter(cr => cr.status === SubscriberRequirementStatus.Pending)
      .flatMap(cr => {
        if (cr.__typename === 'ContractSubscriberRequirement') {
          return [
            {
              displayName: cr.contract.displayName,
              contractId: cr.contract.contractId,
              contractType: cr.contract.contractType,
              tag: cr?.contract?.tag as ContractTag,
              ...cr.contract.currentVersion!,
            },
          ];
        } else {
          return [];
        }
      });

  const notAgencyOrAllowed = (c: ContractVersion) => {
    return c.contractId !== AGENCY_CONTRACT_ID || !NO_AGENCY_STATES.includes(stateId);
  };

  const contracts: ContractVersion[] = unique([
    requiredContractVersions.filter(c => c.signatureType === 'agreement'),
    stateGeneralContractVersions,
    data.taggedContractVersions,
  ])
    .filter(contractVersion => !termsAndPrivacyContractIds.includes(contractVersion?.contractId))
    .filter(notAgencyOrAllowed);

  const docuSignContracts = requiredContractVersions.filter(c => c.signatureType === 'docusign');
  return {
    // The LMI docusign is mutually exclusive with the "normal" DocuSign flow, so adding this check
    // so we don't inadvertently combine a docusign and LMI form into the same envelope.
    // With our current data there shouldn't be a situation where this happens.
    docuSignContracts: data.lmiForm ? [data.lmiForm] : docuSignContracts,
    generalContracts,
    showLmi: !!data.lmiForm,
    vnmSolarProjectContracts: contracts,
  };
};

// address overlap in contracts across state, project, and tagged categories
const unique = (contracts: ContractVersion[][]): ContractVersion[] =>
  uniqBy(contracts.flat(), 'id');

export type ContractVersion = {
  contractId: number;
  contractType: string;
  displayName: string;
  fileUrl: string;
  id: number;
  scrollRequired: boolean;
  tag?: ContractTag | null;
  contractVersionLanguageId?: number | null;
  signatureType?: string;
};

type ContractVersionWithSignature = ContractVersion & {
  signatureType: string;
};

export type FetchVnmTermsParams = {
  stateId: number;
  contractTags?: ContractTag[];
  utilityAccountId: number;
};

export interface FetchVnmTermsResponse {
  docuSignContracts: ContractVersion[];
  showLmi?: boolean;
  generalContracts: ContractVersion[];
  vnmSolarProjectContracts: ContractVersion[];
}
